import {
    FC,
    Suspense,
    useContext,
    useState,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import { graphql, useRefetchableFragment } from 'react-relay';
import classnames from 'classnames';

import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalBackButton } from 'dibs-elements/exports/ModalBackButton';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { Link } from 'dibs-elements/exports/Link';
import { Input } from 'dibs-elements/exports/Input';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { CurrencyInput } from 'dibs-currency-input';
import { TextArea } from 'dibs-elements/exports/TextArea';
import CirclePlus from 'dibs-icons/exports/legacy/CirclePlus';
import CheckmarkCircle from 'dibs-icons/exports/legacy/CheckmarkCircle';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import CreatorsSearch from './sharedComponents/CreatorsSearch';
import CategoryL2L3Select from './sharedComponents/CategoryL2L3Select';
import ItemCondition from './sharedComponents/ItemCondition';
import ImageUpload from './sharedComponents/ImageUpload';
import SellerApplicationAbandonConfirmation from './SellerApplicationAbandonConfirmation';
import InputsWrapper from './sharedComponents/InputsWrapper';
import FormButton from './sharedComponents/FormButton';

import {
    useGetVerticalSpecificField,
    useGetYesNoOptions,
    useGetAttributeOptions,
} from '../helpers/sellerApplicationOptions';

import SellerFormContext from './SellerFormContext';
import { sellerApplicationFormFieldsValidation } from '../helpers/sellerApplicationFormValidator';

import { SellerApplicationFormInventorySample_viewer$key } from './__generated__/SellerApplicationFormInventorySample_viewer.graphql';
import { SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';
import {
    BusinessItem,
    verticalSpecificsMap,
    MATERIALS,
    RESTORATION_AND_MODIFICATIONS,
    MEDIUM,
    MORE_SIZES_AND_EDITIONS,
    METALS,
    STONES,
    STONE_CUT,
    CUSTOMIZABLE,
} from './SellerApplicationFormReducer';

import styles from './styles/SellerApplicationFormInventoryShared.scss';
import dibsCss from 'dibs-css';

const messages = defineMessages({
    abandon: {
        id: 'SellerApplicationForm.inventorySample.abandon',
        defaultMessage: 'Abandon Item Sample?',
    },
    addItemDetails: {
        id: 'SellerApplicationForm.inventorySample.addItemDetails',
        defaultMessage: 'Add Item Details',
    },
    itemTitle: {
        id: 'SellerApplicationForm.inventorySample.itemTitle',
        defaultMessage: 'Item Title',
    },
    category: {
        id: 'SellerApplicationForm.inventorySample.category',
        defaultMessage: 'Item Category',
    },
    subCategory: {
        id: 'SellerApplicationForm.inventorySample.subCategory',
        defaultMessage: 'Item Sub Category',
    },
    description: {
        id: 'SellerApplicationForm.inventorySample.description',
        defaultMessage: 'Description of Item',
    },
    search: {
        id: 'SellerApplicationForm.inventorySample.search',
        defaultMessage: 'Search for Creator/Artist/Designer (Optional)',
    },
    period: {
        id: 'SellerApplicationForm.inventorySample.period',
        defaultMessage: 'Period',
    },
    retailPrice: {
        id: 'SellerApplicationForm.inventorySample.retailPrice',
        defaultMessage: 'Retail Price',
    },
    itemCondition: {
        id: 'SellerApplicationForm.inventorySample.itemCondition',
        defaultMessage: 'Item Condition',
    },
});

const viewerFragment = graphql`
    fragment SellerApplicationFormInventorySample_viewer on Viewer
    @refetchable(queryName: "SellerApplicationFormInventorySampleRefetchQuery")
    @argumentDefinitions(vertical: { type: "VerticalType" }) {
        attributes(vertical: $vertical) {
            material {
                label: name
                value: serviceId
                status
                child {
                    label: name
                    value: serviceId
                    status
                    child {
                        label: name
                        value: serviceId
                        status
                    }
                }
            }
            period {
                label: name
                value: serviceId
                status
                child {
                    label: name
                    value: serviceId
                    status
                    child {
                        label: name
                        value: serviceId
                        status
                        child {
                            label: name
                            value: serviceId
                            status
                        }
                    }
                }
            }
            stoneCut {
                label: name
                value: serviceId
                status
            }
        }
        ...CategoryL2L3Select_viewer
        ...ItemCondition_viewer
    }
`;

type ErrorType = { [fieldName in keyof BusinessItem]?: ReactNode };

type Props = {
    index: number;
    disabled: boolean;
    viewer: SellerApplicationFormInventorySample_viewer$key;
};

const SellerApplicationFormInventorySample: FC<Props> = ({
    index,
    disabled,
    viewer: viewerRef,
}) => {
    const intl = useIntl();
    const { dispatch, state } = useContext(SellerFormContext);
    const [viewer, refetchViewer] = useRefetchableFragment(viewerFragment, viewerRef);
    const [errorMessages, setErrorMessages] = useState<ErrorType>({});
    const [isOpen, setIsOpen] = useState(false);
    const [isAbandonOpen, setIsAbandonOpen] = useState(false);
    const yesNoOptions = useGetYesNoOptions();

    const { vertical } = state;

    const previousItemState = useRef<Partial<BusinessItem> | null>(null);
    const item = state.formDataBusinessInventory.items?.[index] || {};
    const {
        title,
        category,
        subCategory,
        description,
        creatorArtistDesigner,
        materials,
        medium,
        metals,
        stones,
        stoneCut,
        imageFiles,
        price,
        period,
        condition,
        moreSizesAndEditions,
        restorationAndModifications,
        customizable,
    } = item;
    const {
        material: materialAttrArr,
        period: periodAttrArr,
        stoneCut: stoneCutAttrArr,
    } = viewer.attributes || {};
    const metalAttrArr = materialAttrArr?.find(mat => mat?.label === 'Metal')?.child;
    const stoneAttrArr = materialAttrArr?.find(mat => mat?.label === 'Stone')?.child;
    const currency = state.formDataBusinessDetails.averageItemPriceCurrency || 'USD';

    useEffect(() => {
        if (vertical) {
            refetchViewer({ vertical: vertical.toLowerCase() });
        }
    }, [vertical, refetchViewer]);

    const validateField = (
        name: keyof BusinessItem,
        value?: string | string[] | boolean | File[] | SingleOnChangeArgs
    ): void => {
        const { errors } = sellerApplicationFormFieldsValidation({ [name]: value });
        const error = errors?.[name];

        setErrorMessages({
            ...errorMessages,
            [name]: error,
        });
    };

    const onSubmit = async (): Promise<void> => {
        const verticalFields = verticalSpecificsMap[vertical].fields;
        const fieldsToValidate = {
            title,
            category,
            subCategory,
            description,
            ...(creatorArtistDesigner?.label && {
                creatorArtistDesigner: creatorArtistDesigner.label,
            }),
            ...(verticalFields.includes(MATERIALS) && { materials }),
            ...(verticalFields.includes(MEDIUM) && { medium }),
            imageFiles,
            price,
            period,
            condition,
            ...(verticalFields.includes(MORE_SIZES_AND_EDITIONS) && {
                moreSizesAndEditions,
            }),
            ...(verticalFields.includes(RESTORATION_AND_MODIFICATIONS) && {
                restorationAndModifications,
            }),
            ...(verticalFields.includes(CUSTOMIZABLE) && { customizable }),
        };
        const { isValid, errors } = sellerApplicationFormFieldsValidation({
            ...fieldsToValidate,
        });
        if (isValid) {
            setErrorMessages({});
            previousItemState.current = item;
            setIsOpen(false);
        } else {
            setErrorMessages(errors);
        }
    };

    const setFormField = useCallback(
        (
            field: keyof BusinessItem,
            value?: string | string[] | boolean | File[] | SingleOnChangeArgs
        ): void => {
            dispatch({
                type: 'SET_BUSINESS_ITEM_FIELD',
                index,
                fieldValue: { [field]: value },
            });
        },
        [dispatch, index]
    );

    const setImagesField = (value: File[]): void => {
        setFormField('imageFiles', value);
        validateField('imageFiles', value);
    };

    const setField = (
        name: keyof BusinessItem,
        value?: string | string[] | boolean | SingleOnChangeArgs
    ): void => {
        setFormField(name, value);
        validateField(name, value);
    };

    const closeModal = (): void => {
        if (previousItemState.current) {
            dispatch({ type: 'SET_BUSINESS_ITEM', index, item: previousItemState.current });
        } else {
            dispatch({ type: 'REMOVE_BUSINESS_ITEM', index });
        }
        setIsOpen(false);
        setIsAbandonOpen(false);
    };

    const handleOpenModal = (): void => {
        if (!disabled) setIsOpen(true);
    };

    const startAbandon = (): void => setIsAbandonOpen(true);

    const modalTopContent = isAbandonOpen ? (
        <BarHeader title={intl.formatMessage(messages.abandon)} />
    ) : (
        <>
            <BarHeader title={intl.formatMessage(messages.addItemDetails)} />
            <ModalBackButton onClick={startAbandon} />
            <ModalCloseButton onClick={startAbandon} />
        </>
    );

    const modalBodyContent = (
        <div className={dibsCss.wFull}>
            <InputsWrapper>
                <Input
                    placeholder={intl.formatMessage(messages.itemTitle)}
                    hasAnimatedPlaceholder
                    dataTn="item-title"
                    name="item-title"
                    onChange={e => {
                        const value = e.target.value;
                        setField('title', value);
                    }}
                    errorMessage={errorMessages?.title}
                    hasError={!!errorMessages?.title}
                    value={title || ''}
                    maxLength={80}
                />
            </InputsWrapper>
            <CategoryL2L3Select
                viewer={viewer}
                vertical={vertical}
                onChangeL2={option => {
                    setField('category', option?.value || '');
                    setFormField('subCategory', '');
                }}
                valueL2={category || ''}
                errorMessageL2={errorMessages?.category}
                onChangeL3={option => setField('subCategory', option?.value || '')}
                valueL3={subCategory || ''}
                errorMessageL3={errorMessages?.subCategory}
            />
            <InputsWrapper>
                <TextArea
                    placeholder={intl.formatMessage(messages.description)}
                    rows={3}
                    value={description || ''}
                    onChange={e => {
                        const value = e.target.value;
                        setField('description', value);
                    }}
                    errorMessage={errorMessages?.description}
                    dataTn="item-description"
                    maxLength={5000}
                />
            </InputsWrapper>
            <InputsWrapper>
                <CreatorsSearch
                    onChange={option => {
                        setField('creatorArtistDesigner', option);
                    }}
                    vertical={vertical}
                    value={creatorArtistDesigner}
                />
            </InputsWrapper>
            {useGetVerticalSpecificField({
                vertical,
                field: MATERIALS,
                options: useGetAttributeOptions(materialAttrArr),
                value: materials,
                setField,
                errorMessage: errorMessages?.materials,
            })}
            {useGetVerticalSpecificField({
                vertical,
                field: MEDIUM,
                options: useGetAttributeOptions(materialAttrArr),
                value: medium,
                setField,
                errorMessage: errorMessages?.medium,
            })}
            {useGetVerticalSpecificField({
                vertical,
                field: METALS,
                options: useGetAttributeOptions(metalAttrArr),
                values: metals,
                setField,
                errorMessage: errorMessages?.metals,
            })}
            {useGetVerticalSpecificField({
                vertical,
                field: STONES,
                options: useGetAttributeOptions(stoneAttrArr),
                values: stones,
                setField,
                errorMessage: errorMessages?.stones,
            })}
            {useGetVerticalSpecificField({
                vertical,
                field: STONE_CUT,
                options: useGetAttributeOptions(stoneCutAttrArr),
                value: stoneCut,
                setField,
                errorMessage: errorMessages?.stoneCut,
            })}
            <InputsWrapper>
                <BasicSelect
                    dataTn="item-period"
                    name="item-period"
                    placeholder={intl.formatMessage(messages.period)}
                    hasAnimatedPlaceholder
                    options={useGetAttributeOptions(periodAttrArr)}
                    onChange={e => {
                        const value = e.target.value;
                        setField('period', value);
                    }}
                    errorMessage={errorMessages?.period}
                    hasError={!!errorMessages?.period}
                    value={period}
                />
            </InputsWrapper>
            <InputsWrapper>
                <CurrencyInput
                    dataTn="item-price"
                    currency={currency}
                    name="item-price"
                    onChange={(value: string) => {
                        setField('price', value);
                    }}
                    value={price}
                    errorMessage={errorMessages?.price}
                    hasError={!!errorMessages?.price}
                    errorMessageAlignment="left"
                />
            </InputsWrapper>
            <InputsWrapper>
                <Suspense
                    fallback={
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.justifyCenter
                            )}
                        >
                            <Spinner size="tiny" />
                        </div>
                    }
                >
                    <ItemCondition
                        viewer={viewer}
                        vertical={vertical}
                        value={condition || ''}
                        onChange={e => {
                            const value = e?.value || '';
                            setField('condition', value);
                        }}
                        disabled={!vertical}
                        errorMessage={errorMessages?.condition}
                    />
                </Suspense>
            </InputsWrapper>
            {useGetVerticalSpecificField({
                vertical,
                field: RESTORATION_AND_MODIFICATIONS,
                options: yesNoOptions,
                value:
                    typeof restorationAndModifications === 'boolean'
                        ? String(restorationAndModifications)
                        : restorationAndModifications,
                setField,
                errorMessage: errorMessages?.restorationAndModifications,
            })}
            {useGetVerticalSpecificField({
                vertical,
                field: MORE_SIZES_AND_EDITIONS,
                options: yesNoOptions,
                value:
                    typeof moreSizesAndEditions === 'boolean'
                        ? String(moreSizesAndEditions)
                        : moreSizesAndEditions,
                setField,
                errorMessage: errorMessages?.moreSizesAndEditions,
            })}
            {useGetVerticalSpecificField({
                vertical,
                field: CUSTOMIZABLE,
                options: yesNoOptions,
                value: typeof customizable === 'boolean' ? String(customizable) : customizable,
                setField,
                errorMessage: errorMessages?.customizable,
            })}
            <InputsWrapper>
                <ImageUpload
                    multi
                    imageFiles={imageFiles}
                    fieldRequiredErrorMessage={errorMessages?.imageFiles}
                    setFormField={setImagesField}
                />
            </InputsWrapper>
            <FormButton
                isLoading={false}
                onSubmit={onSubmit}
                submitCopy={
                    !previousItemState.current ? (
                        <FormattedMessage
                            defaultMessage="Add Item"
                            id="SellerApplicationForm.inventorySample.addItem"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Update Item"
                            id="SellerApplicationForm.inventorySample.updateItem"
                        />
                    )
                }
            />
        </div>
    );

    return (
        <div data-tn="application-form-inventory-sample">
            <Link
                className={classnames(styles.itemLink, {
                    [styles.disabledItemLink]: !!disabled,
                })}
                onClick={handleOpenModal}
            >
                <div
                    className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.sassyFontBody)}
                >
                    {title ? (
                        <>
                            <CheckmarkCircle className={styles.icon} />
                            {title}
                        </>
                    ) : (
                        <>
                            <CirclePlus className={styles.icon} />
                            <FormattedMessage
                                id="SellerApplicationForm.addItemLabel"
                                defaultMessage="Add Item {itemNum}"
                                values={{ itemNum: index + 1 }}
                            />
                        </>
                    )}
                </div>
                <ArrowRight className={styles.icon} />
            </Link>
            <ModalContainer
                isOpen={isOpen}
                onClose={startAbandon}
                modalSize={isAbandonOpen ? 'default' : 'medium'}
            >
                {modalTopContent}
                <ModalBody>
                    {isAbandonOpen ? (
                        <SellerApplicationAbandonConfirmation
                            display={isAbandonOpen}
                            backModal={() => setIsAbandonOpen(false)}
                            closeModal={closeModal}
                        />
                    ) : (
                        modalBodyContent
                    )}
                </ModalBody>
            </ModalContainer>
        </div>
    );
};

export default SellerApplicationFormInventorySample;
